import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, CheckCircle2, Send, Upload, X, Calendar, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const PublicForm = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [otherValues, setOtherValues] = useState({}); // For "Other" text inputs
  const [fileUploads, setFileUploads] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchForm();
  }, [formId]);

  const fetchForm = async () => {
    try {
      const res = await axios.get(`${API}/forms/public/${formId}`);
      setForm(res.data);
      
      const initialResponses = {};
      res.data.fields?.forEach(field => {
        if (field.field_type === 'checkbox' || field.field_type === 'multiselect') {
          initialResponses[field.id] = [];
        } else {
          initialResponses[field.id] = '';
        }
      });
      setResponses(initialResponses);
    } catch (error) {
      toast.error('Form not found or inactive');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setResponses({
      ...responses,
      [fieldId]: value
    });
  };

  const handleRadioWithOther = (fieldId, value, isOther = false) => {
    if (isOther) {
      setResponses({ ...responses, [fieldId]: 'Other' });
    } else {
      setResponses({ ...responses, [fieldId]: value });
      // Clear other value if selecting a non-other option
      if (otherValues[fieldId]) {
        setOtherValues({ ...otherValues, [fieldId]: '' });
      }
    }
  };

  const handleOtherTextChange = (fieldId, value) => {
    setOtherValues({ ...otherValues, [fieldId]: value });
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    const currentValues = responses[fieldId] || [];
    if (checked) {
      setResponses({
        ...responses,
        [fieldId]: [...currentValues, option]
      });
    } else {
      setResponses({
        ...responses,
        [fieldId]: currentValues.filter(v => v !== option)
      });
    }
  };

  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    setUploadingFiles(prev => ({ ...prev, [fieldId]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/forms/public/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFileUploads(prev => ({
        ...prev,
        [fieldId]: { file_id: res.data.file_id, filename: res.data.filename }
      }));
      
      setResponses(prev => ({
        ...prev,
        [fieldId]: res.data.file_id
      }));
      
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const removeFile = (fieldId) => {
    setFileUploads(prev => {
      const newUploads = { ...prev };
      delete newUploads[fieldId];
      return newUploads;
    });
    setResponses(prev => ({
      ...prev,
      [fieldId]: ''
    }));
    if (fileInputRefs.current[fieldId]) {
      fileInputRefs.current[fieldId].value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare final responses with "Other" values
    const finalResponses = { ...responses };
    Object.keys(otherValues).forEach(fieldId => {
      if (responses[fieldId] === 'Other' && otherValues[fieldId]) {
        finalResponses[fieldId] = `Other: ${otherValues[fieldId]}`;
      }
    });
    
    // Validate required fields
    for (const field of form.fields) {
      if (field.required && !['section', 'form_image'].includes(field.field_type)) {
        const value = finalResponses[field.id];
        if (field.field_type === 'checkbox' && (!value || value.length === 0)) {
          toast.error(`Please select at least one option for "${field.label}"`);
          return;
        } else if (field.field_type !== 'checkbox' && !value) {
          toast.error(`Please fill in "${field.label}"`);
          return;
        }
      }
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API}/forms/public/${formId}/submit`, {
        form_id: form.id,
        responses: finalResponses
      });
      
      setSubmitted(true);
      toast.success('Form submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.field_type) {
      case 'email':
        return (
          <Input
            type="email"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder="Your answer"
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent"
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      case 'phone':
        return (
          <Input
            type="tel"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder="Your answer"
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent"
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder="Your answer"
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent"
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder="Your answer"
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent resize-none"
            rows={3}
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      case 'select':
        return (
          <Select
            value={responses[field.id] || ''}
            onValueChange={(value) => handleInputChange(field.id, value)}
          >
            <SelectTrigger className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent" data-testid={`field-${field.id}`}>
              <SelectValue placeholder="Choose" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option, idx) => (
                <SelectItem key={idx} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'radio':
        const hasOtherOption = field.options?.some(opt => opt.toLowerCase() === 'other' || opt.toLowerCase() === 'other:');
        const regularOptions = field.options?.filter(opt => opt.toLowerCase() !== 'other' && opt.toLowerCase() !== 'other:') || [];
        const showOther = hasOtherOption || field.allow_other;
        
        return (
          <div className="space-y-3 mt-2" data-testid={`field-${field.id}`}>
            {regularOptions.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <input
                  type="radio"
                  id={`${field.id}-${idx}`}
                  name={field.id}
                  value={option}
                  checked={responses[field.id] === option}
                  onChange={() => handleRadioWithOther(field.id, option)}
                  className="h-5 w-5 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor={`${field.id}-${idx}`} className="text-sm text-gray-700 cursor-pointer">
                  {option}
                </label>
              </div>
            ))}
            {showOther && (
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    id={`${field.id}-other`}
                    name={field.id}
                    checked={responses[field.id] === 'Other'}
                    onChange={() => handleRadioWithOther(field.id, 'Other', true)}
                    className="h-5 w-5 text-blue-600 border-2 border-gray-300 focus:ring-blue-500 cursor-pointer"
                  />
                  <label htmlFor={`${field.id}-other`} className="text-sm text-gray-700 cursor-pointer">
                    Other:
                  </label>
                  <Input
                    type="text"
                    value={otherValues[field.id] || ''}
                    onChange={(e) => {
                      handleOtherTextChange(field.id, e.target.value);
                      if (e.target.value) {
                        handleRadioWithOther(field.id, 'Other', true);
                      }
                    }}
                    onFocus={() => handleRadioWithOther(field.id, 'Other', true)}
                    placeholder=""
                    className="flex-1 border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent h-8"
                  />
                </div>
              </div>
            )}
          </div>
        );
      case 'multiselect':
      case 'checkbox':
        return (
          <div className="space-y-3 mt-2" data-testid={`field-${field.id}`}>
            {field.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <Checkbox
                  id={`${field.id}-${idx}`}
                  checked={(responses[field.id] || []).includes(option)}
                  onCheckedChange={(checked) => handleCheckboxChange(field.id, option, checked)}
                  className="h-5 w-5 border-2 border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <label htmlFor={`${field.id}-${idx}`} className="text-sm text-gray-700 cursor-pointer">
                  {option}
                </label>
              </div>
            ))}
          </div>
        );
      case 'date':
        return (
          <Input
            type="date"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent w-48"
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
      case 'file':
        return (
          <div className="mt-2" data-testid={`field-${field.id}`}>
            <p className="text-xs text-gray-500 mb-2">Upload 1 supported file: PDF. Max 10 MB.</p>
            {fileUploads[field.id] ? (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-md">
                  <FileText className="h-4 w-4 text-gray-600" />
                  <span className="text-sm text-gray-700">{fileUploads[field.id].filename}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(field.id)}
                    className="text-gray-500 hover:text-red-500 ml-2"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  ref={el => fileInputRefs.current[field.id] = el}
                  onChange={(e) => handleFileUpload(field.id, e.target.files[0])}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRefs.current[field.id]?.click()}
                  disabled={uploadingFiles[field.id]}
                  className="border-gray-300 text-gray-600 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {uploadingFiles[field.id] ? 'Uploading...' : 'Add File'}
                </Button>
              </div>
            )}
          </div>
        );
      default:
        return (
          <Input
            type="text"
            value={responses[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
            placeholder="Your answer"
            className="border-0 border-b-2 border-gray-200 rounded-none px-0 focus:border-blue-500 focus:ring-0 bg-transparent"
            required={field.required}
            data-testid={`field-${field.id}`}
          />
        );
    }
  };

  // Render description with bullet points support
  const renderDescription = (text) => {
    if (!text) return null;
    
    // Split by newlines and check for bullet points
    const lines = text.split('\n');
    const hasBullets = lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*'));
    
    if (hasBullets) {
      return (
        <ul className="list-disc list-inside space-y-1 text-gray-600">
          {lines.map((line, idx) => {
            const cleanLine = line.trim().replace(/^[•\-\*]\s*/, '');
            if (!cleanLine) return null;
            return <li key={idx} className="text-sm">{cleanLine}</li>;
          })}
        </ul>
      );
    }
    
    return <p className="text-gray-600 text-sm">{text}</p>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f0ebf8] flex items-center justify-center">
        <div className="animate-pulse text-purple-600">Loading form...</div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="min-h-screen bg-[#f0ebf8] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg rounded-lg shadow-md border-0">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-800">Form Not Found</p>
            <p className="text-sm text-gray-500 mt-1">This form may have been deleted or is no longer active.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f0ebf8] flex items-center justify-center p-4">
        <Card className="w-full max-w-lg rounded-lg shadow-md border-0">
          <div className="h-2 bg-purple-600 rounded-t-lg"></div>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <p className="text-2xl font-medium text-gray-800">Your response has been recorded.</p>
            <p className="text-gray-600 mt-3">{form.thank_you_message || 'Thank you for your submission!'}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0ebf8] py-6 px-4">
      <div className="max-w-2xl mx-auto space-y-3">
        {/* Header Image */}
        {form.header_image_id && (
          <div className="w-full h-48 overflow-hidden rounded-lg shadow-md">
            <img 
              src={`${API}/custom-forms/header-image/${form.header_image_id}`}
              alt="Form header"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        {/* Form Title Card */}
        <Card className="rounded-lg shadow-md border-0 overflow-hidden">
          <div className="h-2.5 bg-purple-600"></div>
          <CardContent className="pt-5 pb-6 px-6">
            <h1 className="text-3xl font-normal text-gray-800 mb-3">{form.title}</h1>
            {form.description && (
              <div className="mb-4">
                {renderDescription(form.description)}
              </div>
            )}
            <p className="text-sm text-red-500">* Indicates required question</p>
          </CardContent>
        </Card>
        
        {/* Form Fields */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {form.fields?.map((field) => {
            // Render Section Header
            if (field.field_type === 'section') {
              return (
                <Card key={field.id} className="rounded-lg shadow-md border-0 overflow-hidden">
                  <div className="h-2.5 bg-purple-600"></div>
                  <CardContent className="pt-5 pb-4 px-6">
                    <h2 className="text-2xl font-normal text-gray-800">{field.label}</h2>
                    {field.description && (
                      <p className="text-sm text-gray-600 mt-2">{field.description}</p>
                    )}
                  </CardContent>
                </Card>
              );
            }
            
            // Render In-Form Image
            if (field.field_type === 'form_image' && field.image_id) {
              return (
                <Card key={field.id} className="rounded-lg shadow-md border-0 overflow-hidden">
                  <CardContent className="p-0">
                    <img 
                      src={`${API}/custom-forms/header-image/${field.image_id}`}
                      alt="Form"
                      className="w-full rounded-lg"
                    />
                  </CardContent>
                </Card>
              );
            }
            
            // Skip form_image without image
            if (field.field_type === 'form_image') {
              return null;
            }
            
            // Render Regular Input Field in Card
            return (
              <Card key={field.id} className="rounded-lg shadow-md border-0 border-l-4 border-l-transparent hover:border-l-blue-500 transition-colors">
                <CardContent className="pt-5 pb-6 px-6">
                  <label className="block text-sm font-normal text-gray-800 mb-1">
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {field.description && (
                    <p className="text-xs text-gray-500 mb-2">{field.description}</p>
                  )}
                  {renderField(field)}
                </CardContent>
              </Card>
            );
          })}

          {/* Submit and Clear Buttons */}
          <div className="flex items-center justify-between pt-2">
            <Button 
              type="submit" 
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md shadow-sm"
              disabled={submitting}
              data-testid="submit-form-btn"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
            <button
              type="button"
              onClick={() => {
                setResponses({});
                setOtherValues({});
                setFileUploads({});
              }}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium"
            >
              Clear form
            </button>
          </div>
        </form>
        
        <p className="text-center text-xs text-gray-500 pt-4">
          Never submit passwords through this form.
        </p>
      </div>
    </div>
  );
};

export default PublicForm;
